import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { copyCode } from "@/lib/clipboard";
import { Copy, Sparkles } from "lucide-react";
import { api, couponGoUrl } from "@/lib/api";
import { useSite } from "@/lib/site";
import { SectionHead } from "@/components/site/bits";
import { DemoBadge } from "@/components/site/DealCard";

export const copyAndGo = async (c) => {
  await copyCode(c.code, `Code ${c.code} copied - paste it at checkout`);
  window.open(couponGoUrl(c.id), "_blank", "noopener");
};

const StoreTile = ({ s }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col gap-3 hover:shadow-md transition-shadow" data-testid={`top-store-${s.slug}`}>
    <div className="flex items-center justify-between gap-2">
      <Link to={`/store/${s.slug}`} className="font-extrabold text-slate-900 hover:text-emerald-700 truncate">{s.name}</Link>
      <span className="text-[11px] text-slate-500 whitespace-nowrap">{s.live_codes} {s.live_codes === 1 ? "code" : "codes"}</span>
    </div>
    <p className="text-sm text-slate-600 line-clamp-2 min-h-[2.5rem]">{s.top_code.title}</p>
    <div className="mt-auto space-y-2">
      <div className="flex items-center gap-2">
        <code className="flex-1 truncate font-mono font-bold text-amber-900 bg-amber-50 border border-dashed border-amber-400 px-2 py-1 rounded text-sm text-center">{s.top_code.code}</code>
        <button onClick={() => copyAndGo(s.top_code)} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-2 rounded-lg flex items-center gap-1 transition-colors" data-testid={`top-store-use-${s.slug}`}>
          <Copy size={12} /> Copy
        </button>
      </div>
      <DemoBadge show={s.top_code.is_demo} />
    </div>
  </div>
);

export const TopStores = () => {
  const siteName = useSite().name;
  const { data } = useQuery({ queryKey: ["top-stores"], queryFn: () => api.get("/public/top-stores").then((r) => r.data) });
  if (!data?.length) return null;
  return (
    <section data-testid="section-top-stores">
      <SectionHead title="Top stores & their biggest code" sub={`Trusted stores ranked by shopper clicks on ${siteName} (30 days), then number of live codes. The code shown has the biggest saving stated by the store.`} to="/stores" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">{data.map((s) => <StoreTile key={s.id} s={s} />)}</div>
    </section>
  );
};

export const FestiveBanner = () => {
  const { data } = useQuery({ queryKey: ["festive", "diwali"], queryFn: () => api.get("/public/festive/diwali").then((r) => r.data) });
  if (!data?.coupons?.length) return null;
  return (
    <Link to="/offers/diwali" data-testid="festive-banner" className="block rounded-2xl bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 text-white p-6 sm:p-8 hover:shadow-xl transition-shadow">
      <p className="text-xs uppercase tracking-widest font-bold flex items-center gap-2"><Sparkles size={14} /> Festive offers hub</p>
      <p className="text-2xl sm:text-3xl font-extrabold mt-2">Diwali offers from {data.stores.length} trusted stores</p>
      <p className="text-white/90 mt-1">{data.coupons.length} live festive codes & offers in one place, soonest-ending first →</p>
    </Link>
  );
};
